import { useEffect, useState } from 'react';
import { useGetApiData } from '@grc/ui-utility';
import { BusinessEntityType } from '@grc/ui-shared/lib/components/models/enums/enums';
import {
  GrcButton,
  FontAwesomeIcon,
  IconProp,
  faarrowleftlongclassicregular
} from '@grc/ui-package';
import { useI18nState } from '../../../stores/settings/language-context';
import GetShipmentDetails from '../../../service/api/get-shipment-details-service';
import { fetchBusinessEntities } from '../../../service/api/fetch-business-entities';
import { getAnySettings } from '../../../service/api/get-any-settings';
import { orderSettingUrl } from '../../../common/constants/url-constants';
import { orderService } from '../../../common/constants/constants';
import {
  IShipmentDetail,
  IShipmentDetailSettings,
  IShipmentLocation
} from '../../../models/shipments/shipment-details.interface';
import LocationCard from '../shipment-details/tabs/details/location-card/location-card';
import HandlingUnitTable from '../shipment-details/tabs/details/handling-unit-table/handling-unit-table';
import useDetailsTab from '../shipment-details/tabs/details/useDetailsTab';
import useTrackingUpdates from '../shipment-details/tracking-updates/useTrackingUpdates';
import TrackingUpdatesDrawer from '../shipment-details/tracking-updates/tracking-updates-drawer';
import { getShipmentStatusLabel } from '../shipment-status';
import Loader from '../../../common/loader';

import './shipment-pin-details-panel.scss';

interface IShipmentPinDetailsPanelProps {
  shipmentNumber: string;
  // Already-fetched transport record from useShipmentHeatMapData - not
  // consumed yet (see ModesAndEquipmentCard below), kept on the prop
  // contract so wiring it in later doesn't require touching the caller.
  transport?: any;
  // When true, opens the Tracking Updates drawer automatically once tracking
  // becomes available (set when the card's own tracking-icon button, rather
  // than the card body, was clicked - see shipment-heat-map.tsx's selectPin).
  autoOpenTracking?: boolean;
  onBack: () => void;
  onViewFullDetails: () => void;
}

const emptySettings: IShipmentDetailSettings = {
  handlingUnitTypes: [],
  nmfcs: [],
  accessorials: [],
  referenceTypes: [],
  unitsOfMeasure: []
};

// Modes & Equipment fields (mode, equipment type, trailer/cab/seal no) live
// on the transport record (already fetched by useShipmentHeatMapData, no
// extra call here), but that response is untyped and its exact field names
// aren't confirmed anywhere else in this codebase (see
// docs/shipment-tracking-map-api-gaps.md, gap #5). Rendering blank rather
// than guessing a field name - wire the real path here once confirmed
// against a live GetTransportById response, same precedent as the grid's
// blank TransportId column (shipment-grid.columns.tsx).
function ModesAndEquipmentCard({
  localeData
}: {
  localeData: Record<string, any>;
}) {
  const rows: Array<[string, string | undefined]> = [
    [localeData.TransportationMode, undefined],
    [localeData.EquipmentType, undefined],
    [localeData.TrailerContainerNo, undefined],
    [localeData.CabPowerNo, undefined],
    [localeData.SealNo, undefined],
    [localeData.CustomsSealNo, undefined],
    [localeData.ServiceLevel, undefined],
    [localeData.ServiceType, undefined]
  ];

  return (
    <div className="modes-and-equipment-card">
      <label className="grc-card-title ml-2">
        {localeData.ModesAndEquipment}
      </label>
      <div className="modes-and-equipment-grid">
        {rows.map(([label, value]) => (
          <div className="modes-and-equipment-item" key={label}>
            <span className="grc-text-light">{label}</span>
            <span>{value ?? '-'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Full inline summary panel for a heat map pin/list-row click, mirroring the
// UI reference's shipment-details screenshot. Reuses the existing Shipment
// Details screen's own components (LocationCard, HandlingUnitTable,
// useDetailsTab) rather than re-implementing them, and reuses
// useTrackingUpdates + TrackingUpdatesDrawer unchanged for the "Tracking
// Updates" button - that flow already works end-to-end.
export default function ShipmentPinDetailsPanel({
  shipmentNumber,
  autoOpenTracking,
  onBack,
  onViewFullDetails
}: IShipmentPinDetailsPanelProps) {
  const { localeData } = useI18nState();
  const { fetchData } = useGetApiData();

  const [shipment, setShipment] = useState<IShipmentDetail>();
  const [isLoading, setIsLoading] = useState(true);
  const [origin, setOrigin] = useState<IShipmentLocation>();
  const [destination, setDestination] = useState<IShipmentLocation>();
  const [settings, setSettings] =
    useState<IShipmentDetailSettings>(emptySettings);

  useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      setIsLoading(true);
      const result = await GetShipmentDetails(fetchData, shipmentNumber);
      if (!isCancelled) {
        setShipment(result);
        setIsLoading(false);
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [fetchData, shipmentNumber]);

  useEffect(() => {
    if (!shipment) return;

    let isCancelled = false;

    const load = async () => {
      const locationIds = Array.from(
        new Set(
          [shipment.originId, shipment.destinationId].filter(
            (id): id is string => !!id
          )
        )
      );

      const [entitiesResult, settingsResult] = await Promise.all([
        locationIds.length
          ? fetchBusinessEntities({
              fetchData,
              entityIds: locationIds,
              entityType: BusinessEntityType.Location
            })
          : Promise.resolve({ map: {} as Record<string, IShipmentLocation> }),
        shipment.accountId
          ? getAnySettings(orderSettingUrl, fetchData, orderService, {
              ownerId: shipment.accountId
            })
          : Promise.resolve(undefined)
      ]);

      if (isCancelled) return;

      const map = (entitiesResult?.map ?? {}) as Record<
        string,
        IShipmentLocation
      >;
      setOrigin(shipment.originId ? map[shipment.originId] : undefined);
      setDestination(
        shipment.destinationId ? map[shipment.destinationId] : undefined
      );

      const resolvedSettings: any = settingsResult ?? {};
      setSettings({
        handlingUnitTypes: resolvedSettings?.HandlingUnitTypeDetails ?? [],
        nmfcs: resolvedSettings?.NmfcDetails ?? [],
        accessorials: resolvedSettings?.AccessorialDetails ?? [],
        referenceTypes: resolvedSettings?.ReferenceTypeDetails ?? [],
        unitsOfMeasure: resolvedSettings?.UnitsOfMeasureDetails ?? []
      });
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [fetchData, shipment]);

  const {
    handlingUnitRows,
    totalQuantity,
    totalWeightLabel,
    unitSymbolMap,
    resolveHandlingUnitTypeName,
    resolveNmfcClassName
  } = useDetailsTab(shipment, settings);

  const {
    isOpen,
    isTrackingEnabled,
    openDrawer,
    closeDrawer,
    stops,
    statusUpdates
  } = useTrackingUpdates(shipment);

  // Opens straight into Tracking Updates once it becomes available, when the
  // panel was opened via the list card's tracking-icon shortcut rather than
  // the card body. isTrackingEnabled only turns true after `shipment` loads
  // (see useTrackingUpdates.ts), so this can't just run once on mount.
  useEffect(() => {
    if (autoOpenTracking && isTrackingEnabled && !isOpen) {
      openDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenTracking, isTrackingEnabled]);

  return (
    <>
      <div className="shipment-pin-details-panel">
        <div className="shipment-pin-details-panel-header d-flex align-items-center">
          <button
            type="button"
            className="shipment-pin-details-panel-back"
            aria-label={localeData.GoBack}
            onClick={onBack}
          >
            <FontAwesomeIcon icon={faarrowleftlongclassicregular as IconProp} />
          </button>
          <div>
            <span className="grc-b">{shipmentNumber}</span>
            {shipment?.status !== undefined && (
              <span className="ms-2">
                {getShipmentStatusLabel(shipment.status)}
              </span>
            )}
          </div>
        </div>

        {isLoading && <Loader />}

        {!isLoading && shipment && (
          <div className="shipment-pin-details-panel-body">
            <div className="d-flex shipment-pin-details-panel-locations">
              <LocationCard title={localeData.Origin} data={origin} />
              <LocationCard title={localeData.Destination} data={destination} />
            </div>

            <HandlingUnitTable
              shipmentNumber={shipmentNumber}
              rows={handlingUnitRows}
              totalQuantity={totalQuantity}
              totalWeightLabel={totalWeightLabel}
              unitSymbolMap={unitSymbolMap}
              resolveHandlingUnitTypeName={resolveHandlingUnitTypeName}
              resolveNmfcClassName={resolveNmfcClassName}
            />

            <ModesAndEquipmentCard localeData={localeData} />
          </div>
        )}

        <div className="shipment-pin-details-panel-footer d-flex justify-content-end">
          <GrcButton
            id="heat_map_tracking_updates_button"
            className="grc-btn-primary"
            text={localeData.TrackingUpdates}
            onClick={openDrawer}
            disabled={!isTrackingEnabled}
          />
          <GrcButton
            id="heat_map_view_full_details_button"
            className="grc-btn-secondary ms-2"
            text={localeData.ViewFullDetails}
            onClick={onViewFullDetails}
          />
        </div>
      </div>

      {isOpen && (
        <TrackingUpdatesDrawer
          isOpen={isOpen}
          onClose={closeDrawer}
          shipmentNumber={shipmentNumber}
          stops={stops}
          statusUpdates={statusUpdates}
        />
      )}
    </>
  );
}
